import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, TextField, MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import KenButton from '../../../../../../GlobalComponents/KenButton';
import { getUserDetails } from '../../../../../../storageService';
import { createForm, addQuestion as addFormQuestion, addOption as addFormOption, publishForm, fetchAssessmentForms } from '../../../Helper/AssessmentFormsHelper';
import { enqueueSnackbar } from 'notistack';
import routes from '../../../../../../Utils/routes';
import { fetchFacultyCourses, FacultyCourse } from '../List/FacultyActivities';
import { invalidateFacultyActivitiesCache } from '../List/FacultyActivitiesData';

const useStyles = makeStyles((theme: any) => ({
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.KenColors.CttPrimary ?? '#2563EB',
        cursor: 'pointer',
        marginBottom: '16px',
    },
    card: {
        background: '#fff',
        borderRadius: '18px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(20, 40, 92, 0.05)',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '26px',
    },
    sectionLabel: {
        fontSize: '12px',
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        marginBottom: '16px',
    },
    fieldLabel: {
        fontSize: '13px',
        color: '#8A94A4',
        marginBottom: '8px',
    },
    row: {
        display: 'flex',
        gap: '20px',
        marginBottom: '22px',
    },
    field: {
        flex: 1,
    },
    emptyState: {
        textAlign: 'center',
        padding: '32px 0 38px',
        color: '#8A94A4',
        fontSize: '14px',
    },
    questionRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '16px 4px',
        borderBottom: '1px solid #F3F4F6',
    },
    questionIndex: {
        color: '#6B7280',
        fontWeight: 700,
        width: '22px',
        flex: 'none',
    },
    typePill: {
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '13px',
        fontWeight: 500,
        display: 'inline-block',
        flex: 'none',
    },
    addQuestionSection: {
        borderTop: '1px solid #ECEFF5',
        marginTop: '8px',
        paddingTop: '24px',
    },
    infobox: {
        background: '#E9F2FB',
        borderRadius: '14px',
        padding: '14px 18px',
        marginTop: '16px',
        fontSize: '13px',
        color: '#2380C4',
        lineHeight: 1.55,
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        borderTop: '1px solid #F3F4F6',
        marginTop: '16px',
        fontSize: '13px',
        color: '#6B7280',
    },
    fieldInput: {
        '& .MuiOutlinedInput-root': {
            background: '#F5F7FB',
            borderRadius: '10px',
            '& fieldset': { borderColor: '#E8ECF4' },
            '&:hover fieldset': { borderColor: '#D6DFEC' },
        },
    },
}));

interface DraftQuestion {
    text: string;
    answerType: 'mcq' | 'text';
    options: string[];
}

const emptyDraft = (): DraftQuestion => ({ text: '', answerType: 'mcq', options: [] });

// Pre & Post test forms are ungraded knowledge-check forms, backed by the
// Salesforce AssessmentForms API. "Multiple Choice" is confirmed verbatim
// from the API docs' addQuestion sample payload; open-text's exact expected
// string is NOT documented anywhere — "Open Text" is a plausible guess
// matching the confirmed Title-Case convention, but unverified against a
// live call. If open-text questions 400, this is the first thing to check.
const ANSWER_TYPE_MAP: Record<DraftQuestion['answerType'], string> = {
    mcq: 'Multiple Choice',
    text: 'Open Text',
};

const AssessmentCreate: React.FC = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [formType, setFormType] = useState<'Pre-Test' | 'Post-Test'>('Pre-Test');
    const [courses, setCourses] = useState<FacultyCourse[]>([]);
    // Training the Batch dropdown is narrowed to — batch numbers (e.g.
    // "Batch 1") repeat across trainings, so picking the training first
    // avoids creating a form against the wrong same-numbered batch.
    const [selectedCourseName, setSelectedCourseName] = useState('');
    const [batchProgramPlanId, setBatchProgramPlanId] = useState('');
    // A batch may only have one Pre-test and one Post-test form — tracks
    // which of those two types already exist for the selected batch so the
    // dropdown can block re-creating one.
    const [existingTypes, setExistingTypes] = useState<Set<string>>(new Set());
    const [testName, setTestName] = useState('');
    const [questions, setQuestions] = useState<DraftQuestion[]>([]);
    const [draft, setDraft] = useState<DraftQuestion>(emptyDraft());
    const [saving, setSaving] = useState(false);
    // Once createForm succeeds, reuse this id on any subsequent Save/Publish
    // click instead of calling createForm again — prevents duplicate forms
    // if a later step (adding a question, publishing) fails and the user
    // retries, and disables further saving once the form is truly done.
    const [savedFormId, setSavedFormId] = useState<string | null>(null);
    const [formSaved, setFormSaved] = useState(false);
    // React state updates (saving/formSaved) aren't synchronous, so a fast
    // double-click (or a double-fired click event) can invoke handleSave
    // twice before either render commits disabled=true — a ref flips
    // immediately, so the second concurrent call is rejected right away
    // regardless of render timing.
    const savingRef = useRef(false);

    useEffect(() => {
        const load = async () => {
            const courseData = await fetchFacultyCourses();
            setCourses(courseData);
            const first = courseData.find((c) => c.ProgramPlanId);
            if (first?.ProgramPlanId) setBatchProgramPlanId(first.ProgramPlanId);
            if (first?.CourseName) setSelectedCourseName(first.CourseName);
        };
        load();
    }, []);

    // Distinct trainings across every batch the faculty teaches.
    const courseNames = useMemo(
        () => Array.from(new Set(courses.map((c) => c.CourseName).filter(Boolean))),
        [courses]
    );

    // The Batch dropdown only offers batches for the chosen Training,
    // sorted ascending (numeric-aware) so "Batch 2" comes before "Batch 10".
    const coursesForSelectedCourseName = useMemo(
        () => courses
            .filter((c) => c.CourseName === selectedCourseName && c.ProgramPlanId)
            .sort((a, b) => a.TermName.localeCompare(b.TermName, undefined, { numeric: true, sensitivity: 'base' })),
        [courses, selectedCourseName]
    );

    // Switching Training resets the batch pick to that training's first
    // batch, so the dropdown never keeps a stale pick from a different one.
    useEffect(() => {
        if (!selectedCourseName) return;
        if (!coursesForSelectedCourseName.some((c) => c.ProgramPlanId === batchProgramPlanId)) {
            setBatchProgramPlanId((coursesForSelectedCourseName[0]?.ProgramPlanId as string) || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coursesForSelectedCourseName]);

    useEffect(() => {
        const loadExistingTypes = async () => {
            const contactId = getUserDetails()?.ContactId;
            if (!contactId || !batchProgramPlanId) {
                setExistingTypes(new Set());
                return;
            }
            const forms = await fetchAssessmentForms(batchProgramPlanId, contactId, 'Faculty');
            setExistingTypes(new Set(forms.map((f) => f.formType)));
        };
        loadExistingTypes();
    }, [batchProgramPlanId]);

    // If the batch changes and the currently-picked type is now taken,
    // fall back to whichever of the two types is still free.
    useEffect(() => {
        if (existingTypes.has(formType)) {
            const fallback = (['Pre-Test', 'Post-Test'] as const).find((t) => !existingTypes.has(t));
            if (fallback) setFormType(fallback);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingTypes]);

    const addQuestion = () => {
        if (!draft.text.trim()) {
            enqueueSnackbar('Type the question first.', { variant: 'warning' });
            return;
        }
        if (draft.answerType === 'mcq' && draft.options.filter((o) => o.trim()).length < 2) {
            enqueueSnackbar('Multiple-choice needs at least two options (one per line).', { variant: 'warning' });
            return;
        }
        setQuestions((prev) => [...prev, draft]);
        setDraft(emptyDraft());
    };

    const removeQuestion = (index: number) => {
        setQuestions((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async (publish: boolean) => {
        if (savingRef.current || formSaved) return;
        const contactId = getUserDetails()?.ContactId;
        if (!testName.trim()) {
            enqueueSnackbar('Give the form a title before saving.', { variant: 'warning' });
            return;
        }
        if (!contactId || !batchProgramPlanId) {
            enqueueSnackbar('Select a batch before saving.', { variant: 'warning' });
            return;
        }
        // A batch may only have one form of each type — re-checked here (not
        // just disabled in the dropdown) in case it became stale, e.g.
        // another faculty member created one while this page was open.
        if (existingTypes.has(formType)) {
            enqueueSnackbar(`This batch already has a ${formType} form — only one is allowed per batch.`, { variant: 'error' });
            return;
        }
        // publishForm requires at least one question already added — catch
        // this client-side instead of letting the backend 400. A question
        // typed into the draft fields below but never added via "Add
        // question" doesn't count.
        if (publish && questions.length === 0) {
            enqueueSnackbar('Add at least one question before publishing — click "Add question" first.', { variant: 'warning' });
            return;
        }
        savingRef.current = true;
        try {
            setSaving(true);
            // createForm/addQuestion/addOption all return {success, recordId} —
            // recordId is the generic "id of whatever was just created" field,
            // not a type-specific formId/questionId (confirmed via a live call).
            let formId = savedFormId;
            if (!formId) {
                const formRes = await createForm({
                    contactId,
                    batchId: batchProgramPlanId,
                    formType,
                    title: testName,
                    unlockRule: 'Immediate',
                });
                formId = formRes?.recordId || formRes?.RecordId || null;
                if (!formId) {
                    enqueueSnackbar('Form was not created — please try again.', { variant: 'error' });
                    return;
                }
                setSavedFormId(formId);
            }
            for (let i = 0; i < questions.length; i++) {
                const q = questions[i];
                const questionRes = await addFormQuestion({
                    contactId,
                    formId,
                    questionText: q.text,
                    answerType: ANSWER_TYPE_MAP[q.answerType],
                    order: i + 1,
                    isRequired: true,
                });
                const questionId = questionRes?.recordId || questionRes?.RecordId;
                if (q.answerType === 'mcq' && questionId) {
                    const optionLines = q.options.filter((o) => o.trim());
                    for (let j = 0; j < optionLines.length; j++) {
                        await addFormOption({
                            contactId,
                            questionId,
                            optionText: optionLines[j],
                            order: j + 1,
                        });
                    }
                }
            }
            setQuestions([]);
            if (publish) {
                await publishForm({ contactId, formId });
            }
            setFormSaved(true);
            invalidateFacultyActivitiesCache(contactId);
            enqueueSnackbar(publish ? `${formType} published successfully` : 'Draft saved', { variant: 'success' });
            navigate(routes.AssessmentAdding);
        } catch (error) {
            enqueueSnackbar('Something went wrong', { variant: 'error' });
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return (
        <Box>
            <Typography className={classes.backLink} onClick={() => navigate(routes.AssessmentAdding)}>
                <ArrowBackIcon fontSize="small" /> Back to activities
            </Typography>

            <Box className={classes.card}>
                <Box className={classes.cardHeader}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>New Pre / Post test form</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <KenButton variant="contained" sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }} disabled={saving || formSaved || existingTypes.has(formType)} onClick={() => handleSave(true)}>
                            Publish form
                        </KenButton>
                    </Box>
                </Box>

                <Box className={classes.row}>
                    <Box className={classes.field}>
                        <Typography className={classes.fieldLabel}>Form type</Typography>
                        <TextField className={classes.fieldInput} select fullWidth value={formType} onChange={(e) => setFormType(e.target.value as 'Pre-Test' | 'Post-Test')}>
                            <MenuItem value="Pre-Test" disabled={existingTypes.has('Pre-Test')}>
                                Pre-test{existingTypes.has('Pre-Test') ? ' (already exists for this batch)' : ''}
                            </MenuItem>
                            <MenuItem value="Post-Test" disabled={existingTypes.has('Post-Test')}>
                                Post-test{existingTypes.has('Post-Test') ? ' (already exists for this batch)' : ''}
                            </MenuItem>
                        </TextField>
                        {existingTypes.has('Pre-Test') && existingTypes.has('Post-Test') && (
                            <Typography sx={{ fontSize: '12px', color: '#DC2626', marginTop: '6px' }}>
                                This batch already has both a Pre-test and a Post-test form.
                            </Typography>
                        )}
                    </Box>
                    <Box className={classes.field}>
                        <Typography className={classes.fieldLabel}>Training</Typography>
                        <TextField className={classes.fieldInput} select fullWidth value={selectedCourseName} onChange={(e) => setSelectedCourseName(e.target.value)}>
                            {courseNames.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box className={classes.field}>
                        <Typography className={classes.fieldLabel}>Batch</Typography>
                        <TextField className={classes.fieldInput} select fullWidth disabled={!selectedCourseName} value={batchProgramPlanId} onChange={(e) => setBatchProgramPlanId(e.target.value)}>
                            {coursesForSelectedCourseName.map((course) => (
                                <MenuItem key={course.CourseOfferingID} value={course.ProgramPlanId}>
                                    {course.TermName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </Box>
                <Box sx={{ marginBottom: '4px' }}>
                    <Typography className={classes.fieldLabel}>Form title</Typography>
                    <TextField
                        className={classes.fieldInput}
                        fullWidth
                        placeholder="e.g. Welder — entry knowledge check"
                        value={testName}
                        onChange={(e) => setTestName(e.target.value)}
                    />
                </Box>
            </Box>

            <Box className={classes.card}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
                    Questions
                </Typography>

                {questions.length === 0 ? (
                    <Typography className={classes.emptyState}>
                        No questions yet — add multiple-choice or open-text questions below.
                    </Typography>
                ) : (
                    questions.map((q, index) => (
                        <Box key={index} className={classes.questionRow}>
                            <Typography className={classes.questionIndex}>{index + 1}.</Typography>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '14px' }}>{q.text}</Typography>
                                {q.answerType === 'mcq' && (
                                    <Typography sx={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                                        {q.options.filter(Boolean).join('   ')}
                                    </Typography>
                                )}
                            </Box>
                            <span className={classes.typePill} style={q.answerType === 'mcq' ? { background: '#E9F2FB', color: '#2380C4' } : { background: '#F3F4F6', color: '#6B7280' }}>
                                {q.answerType === 'mcq' ? 'Multiple choice' : 'Open text'}
                            </span>
                            <KenButton variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: '8px' }} onClick={() => removeQuestion(index)}>
                                Remove
                            </KenButton>
                        </Box>
                    ))
                )}

                <Box className={classes.addQuestionSection}>
                    <Typography className={classes.sectionLabel} sx={{ marginBottom: '12px' }}>Add a question</Typography>
                    <Box sx={{ marginBottom: '16px' }}>
                        <Typography className={classes.fieldLabel}>Question</Typography>
                        <TextField
                            className={classes.fieldInput}
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder="Type the question..."
                            value={draft.text}
                            onChange={(e) => setDraft((prev) => ({ ...prev, text: e.target.value }))}
                        />
                    </Box>
                    <Box sx={{ marginBottom: '16px' }}>
                        <Typography className={classes.fieldLabel}>Answer type</Typography>
                        <TextField
                            className={classes.fieldInput}
                            select
                            fullWidth
                            value={draft.answerType}
                            onChange={(e) => setDraft((prev) => ({ ...prev, answerType: e.target.value as 'mcq' | 'text' }))}
                        >
                            <MenuItem value="mcq">Multiple choice</MenuItem>
                            <MenuItem value="text">Open text</MenuItem>
                        </TextField>
                    </Box>
                    {draft.answerType === 'mcq' && (
                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography className={classes.fieldLabel}>Options — one per line (multiple choice only)</Typography>
                            <TextField
                                className={classes.fieldInput}
                                fullWidth
                                multiline
                                minRows={3}
                                placeholder={'Option A\nOption B\nOption C'}
                                value={draft.options.join('\n')}
                                onChange={(e) => setDraft((prev) => ({ ...prev, options: e.target.value.split('\n') }))}
                            />
                        </Box>
                    )}
                    <KenButton variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none', borderRadius: '8px' }} onClick={addQuestion}>
                        Add question
                    </KenButton>
                </Box>

                <Box className={classes.infobox}>
                    Add as many multiple-choice or open-text questions as you need, then <b>Save draft</b> or <b>Publish form</b>. These forms are <b>not scored</b> — they capture where each trainee stands at the start or end of the batch.
                </Box>
            </Box>
        </Box>
    );
};

export default AssessmentCreate;
